/**
 * Kosh contracts API — typed wrappers over /api/v1/kosh/contracts (+ milestones).
 * apiClient unwraps the { success, data } envelope.
 */
import { apiClient } from './api-client';
import type { Paginated } from '@/types/kosh';
import type {
  ContractMilestone,
  KoshContract,
  KoshContractStatus,
} from '@/types/contracts';

type Query = Record<string, string | number | boolean | undefined>;

function rows<T>(data: Paginated<T> | T[] | null | undefined): T[] {
  if (!data) return [];
  return Array.isArray(data) ? data : data.items;
}

export const contractsApi = {
  list: async (params?: Query): Promise<KoshContract[]> => {
    const { data } = await apiClient.get<Paginated<KoshContract> | KoshContract[]>(
      '/kosh/contracts',
      { params },
    );
    return rows<KoshContract>(data);
  },
  get: (id: string) =>
    apiClient.get<KoshContract>(`/kosh/contracts/${id}`).then((r) => r.data),
  transition: (id: string, to: KoshContractStatus) =>
    apiClient
      .post<KoshContract>(`/kosh/contracts/${id}/transitions`, { to })
      .then((r) => r.data),
  milestones: (id: string) =>
    apiClient
      .get<ContractMilestone[]>(`/kosh/contracts/${id}/milestones`)
      .then((r) => r.data),
  acceptMilestone: (id: string, mid: string) =>
    apiClient
      .post(`/kosh/contracts/${id}/milestones/${mid}/accept`, {})
      .then((r) => r.data),
  billMilestone: (id: string, mid: string) =>
    apiClient
      .post(`/kosh/contracts/${id}/milestones/${mid}/bill`, {})
      .then((r) => r.data),
};
